import React, { useState, useMemo } from 'react';
import {
  Container, Typography, Box, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Drawer, List, ListItem, ListItemText, Skeleton,
} from '@mui/material';
import {
  Add, Search, Edit, Delete, Business, Email, Phone, FolderOpen, Close as CloseIcon,
} from '@mui/icons-material';
import { useProjects } from '../context_providers/ProjectContext';
import { useNotification } from '../context_providers/NotificationContext';
import { Client } from '../../types';
import apiService from '../../utils/api.service';
import LoadingSpinner from '../shared/LoadingSpinner';

interface ClientForm {
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  notes: string;
}

const emptyForm: ClientForm = {
  name: '', contactName: '', contactEmail: '', contactPhone: '', address: '', notes: '',
};

const ClientsPage: React.FC = () => {
  const { clients, isLoading, createClient, updateClient, deleteClient } = useProjects();
  const { showSuccess, showError } = useNotification();

  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [projectsDrawerOpen, setProjectsDrawerOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientProjects, setClientProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const filteredClients = useMemo(() => {
    if (!searchQuery) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.contactName?.toLowerCase().includes(q) ||
      c.contactEmail?.toLowerCase().includes(q)
    );
  }, [clients, searchQuery]);

  const handleOpenCreate = () => {
    setEditingClient(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (client: Client) => {
    setEditingClient(client);
    setForm({
      name: client.name,
      contactName: client.contactName || '',
      contactEmail: client.contactEmail || '',
      contactPhone: client.contactPhone || '',
      address: client.address || '',
      notes: client.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        contactName: form.contactName.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
      };

      if (editingClient) {
        await updateClient(editingClient.id, payload);
        showSuccess('Client updated successfully');
      } else {
        await createClient(payload);
        showSuccess('Client created successfully');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingClient(null);
    } catch (err: any) {
      showError(err.message || 'Failed to save client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewProjects = async (client: Client) => {
    setSelectedClient(client);
    setProjectsDrawerOpen(true);
    setProjectsLoading(true);
    try {
      const response = await apiService.getClientProjects(client.id);
      const data = response.data || response;
      setClientProjects(Array.isArray(data) ? data : (data?.projects || []));
    } catch (err: any) {
      showError(err.message || 'Failed to load client projects');
      setClientProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleConfirmDelete = (client: Client) => {
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    try {
      await deleteClient(clientToDelete.id);
      showSuccess('Client deleted');
      setDeleteConfirmOpen(false);
      setClientToDelete(null);
    } catch (err: any) {
      showError(err.message || 'Failed to delete client');
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading clients..." />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Clients</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {clients.length} client{clients.length !== 1 ? 's' : ''} total
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
          New Client
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>

      {/* Table */}
      {filteredClients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Business sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No clients found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            {clients.length === 0 ? 'Add your first client to get started.' : 'Try adjusting your search.'}
          </Typography>
          {clients.length === 0 && (
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
              Add Client
            </Button>
          )}
        </Box>
      ) : (
        <TableContainer component={Card} sx={{ border: '1px solid rgba(148, 163, 184, 0.08)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact Name</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Projects</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients.map(client => (
                <TableRow
                  key={client.id}
                  hover
                  sx={{ '&:last-child td': { border: 0 }, opacity: client.isActive ? 1 : 0.5 }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{client.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {client.contactName || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {client.contactEmail ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.secondary">{client.contactEmail}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">--</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {client.contactPhone ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.secondary">{client.contactPhone}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">--</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      startIcon={<FolderOpen />}
                      onClick={() => handleViewProjects(client)}
                      sx={{ textTransform: 'none' }}
                    >
                      View
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={client.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={client.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEdit(client)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleConfirmDelete(client)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingClient ? 'Edit Client' : 'New Client'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          <TextField
            label="Client Name"
            required
            fullWidth
            autoFocus
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            label="Contact Name"
            fullWidth
            value={form.contactName}
            onChange={(e) => setForm(prev => ({ ...prev, contactName: e.target.value }))}
          />
          <TextField
            label="Contact Email"
            type="email"
            fullWidth
            value={form.contactEmail}
            onChange={(e) => setForm(prev => ({ ...prev, contactEmail: e.target.value }))}
          />
          <TextField
            label="Phone"
            fullWidth
            value={form.contactPhone}
            onChange={(e) => setForm(prev => ({ ...prev, contactPhone: e.target.value }))}
          />
          <TextField
            label="Address"
            fullWidth
            multiline
            rows={2}
            value={form.address}
            onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
          />
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={form.notes}
            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name.trim() || submitting}
          >
            {submitting ? 'Saving...' : editingClient ? 'Update Client' : 'Create Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{clientToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Client Projects Drawer */}
      <Drawer
        anchor="right"
        open={projectsDrawerOpen}
        onClose={() => setProjectsDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {selectedClient?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">Projects</Typography>
            </Box>
            <IconButton onClick={() => setProjectsDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {projectsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={64} />
              ))}
            </Box>
          ) : clientProjects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <FolderOpen sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No projects found for this client.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {clientProjects.map((project: any) => (
                <ListItem
                  key={project.id}
                  sx={{
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    borderRadius: 1, mb: 1, px: 2,
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: project.color || '#7b6df6', mr: 1.5, flexShrink: 0 }} />
                  <ListItemText
                    primary={project.name}
                    secondary={
                      <>
                        {project.code && <span>{project.code} &middot; </span>}
                        <Chip
                          label={project.status || 'active'}
                          size="small"
                          color={project.status === 'active' ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: 500, variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </Container>
  );
};

export default ClientsPage;
